import { useGetShortListedApplicantsQuery } from "@/app/features/recruitment/job-applicants/job-applicants.api";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { DISCLOSER_KEYS, FILTER_KEYS } from "@/constants/keys.constants";
import { useAppDiscloser, useAppFilters } from "@/hooks";
import { StarIcon, UserIcon } from "@/icons";
import { Button, Flex, type TableProps } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import SetupInterview from "../Interviews/_components/SetupInterview";
import AssignModal from "./_components/Shortlisted/AssignModal";
import StageSelector from "./_components/Shortlisted/StageSelector";
import { normalizeColumnValue } from "@/utils";

const JOP_SHORT_STATUS_CONFIG = [
  { label: "Pending", value: "pending", className: "status status-yellow" },
  {
    label: "In Progress",
    value: "in_progress",
    className: "status status-yellow",
  },
  { label: "Submitted", value: "submitted", className: "status status-sky" },
  { label: "Passed", value: "passed", className: "status status-success" },
  { label: "Failed", value: "failed", className: "status status-error" },
  { label: "No Show", value: "no_show", className: "status status-error" },
];

export default function Shortlisted() {
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job_id");
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [selectedStage, setSelectedStage] = useState<
    "assessment" | "interview"
  >("assessment");

  const { page, limit, handlePagination, filters, keyword, handleFilter } =
    useAppFilters(FILTER_KEYS.JOB_SHORTLISTED);

  const { data: shortlistedData, isFetching } =
    useGetShortListedApplicantsQuery({
      params: {
        page,
        limit,
        ...filters,
        search: keyword,
      },
    });

  const [config, handleOpen, handleClose] = useAppDiscloser<
    "ASSIGN_INTERVIEW" | "ASSIGN_ASSESSMENT"
  >(DISCLOSER_KEYS.JOB_SHORTLISTED);

  useEffect(() => {
    if (jobId) {
      handleFilter("job_id", jobId);
      params.delete("job_id");
      setParams(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const columns: TableProps<any>["columns"] = [
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Applicants Name",
      dataIndex: "applicant_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Position",
      dataIndex: "designation_name",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: (
        <div>
          <span className="block">Source</span>
          <span className="text-nowrap block"> (Direct / TalentHunt)</span>
        </div>
      ),
      dataIndex: "is_from_talent_hunt",
      align: "center",
      render: (isTalentHunt: boolean) => (
        <Flex justify="center">
          {isTalentHunt ? (
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-theme-primary/20 to-theme-primary/10 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
              <Flex
                align="center"
                gap={6}
                className="relative bg-theme-primary/10 border border-theme-primary/30 px-3 py-1 rounded-lg backdrop-blur-md"
              >
                <div className="relative flex items-center justify-center">
                  <StarIcon
                    size={14}
                    className="text-theme-primary animate-[spin_3s_linear_infinite]"
                  />
                  <div className="absolute inset-0 bg-theme-primary/20 blur-sm rounded-full animate-pulse"></div>
                </div>
                <span className="text-theme-primary text-[10px] font-black uppercase tracking-[0.15em] drop-shadow-[0_0_8px_rgba(var(--theme-primary-rgb),0.5)]">
                  Hunt
                </span>
              </Flex>
            </div>
          ) : (
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-theme-gray-700/20 to-theme-gray-600/10 rounded-lg blur opacity-10 group-hover:opacity-25 transition duration-1000 group-hover:duration-200"></div>
              <Flex
                align="center"
                gap={6}
                className="relative bg-theme-gray-800/20 border border-theme-gray-700/50 px-3 py-1 rounded-lg backdrop-blur-md"
              >
                <div className="relative flex items-center justify-center">
                  <UserIcon size={14} className="text-theme-gray-300" />
                </div>
                <span className="text-theme-gray-300 text-[10px] font-black uppercase tracking-[0.15em]">
                  Direct
                </span>
              </Flex>
            </div>
          )}
        </Flex>
      ),
    },

    {
      title: "Method",
      dataIndex: "is_force_shortlisted",
      align: "center",
      render: (isForce: boolean) => (
        <Flex justify="center">
          {isForce ? (
            <span className="status-yellow status">Forced Shortlisted</span>
          ) : (
            <span className="status-success status">Auto Shortlisted</span>
          )}
        </Flex>
      ),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Expected Salary",
      dataIndex: "expected_salary",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, 0),
    },
    {
      title: "Assessment",
      dataIndex: "assessment_status",
      align: "center",
      render: (val: string) => (
        <StatusTag value={val} config={JOP_SHORT_STATUS_CONFIG} />
      ),
    },
    {
      title: "Interview",
      dataIndex: "interview_status",
      align: "center",
      render: (val: string) => (
        <StatusTag value={val} config={JOP_SHORT_STATUS_CONFIG} />
      ),
    },
    // {
    //   title: 'Actions',
    //   dataIndex: 'id',
    //   align: 'end',
    //   render: () => (
    //     <Space size={8}>
    //       <Button
    //         size="small"
    //         className="action-btn"
    //         icon={<EyeIcon size={20} />}
    //       />
    //       <Button
    //         size="small"
    //         className="action-btn"
    //         icon={<TrashIcon size={20} />}
    //       />
    //     </Space>
    //   ),
    // },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={shortlistedData?.data?.data || []}
        onPaginate={handlePagination}
        customTitle="Shortlisted"
        loading={isFetching}
        total={shortlistedData?.data?.meta?.total || 0}
        tableRootClassName="border-0 p-0"
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          getCheckboxProps: (record) => ({
            disabled:
              record?.assessment_status !== "pending" ||
              record?.interview_status !== "pending",
            className: "custom-check",
          }),
          columnWidth: 40,
        }}
        filterSection={
          <>
            {/* <Button
              size="middle"
              disabled={!selectedData?.length}
              className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
              onClick={() =>
                handleOpen({
                  type: 'ADD',
                  data: shortlistedData?.data?.data?.filter((item) =>
                    selectedData.includes(item.id),
                  ),
                })
              }
            >
              Move to Next Stage
            </Button> */}
            <Button
              size="middle"
              disabled={!selectedData?.length}
              className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
              onClick={() =>
                handleOpen({
                  type: "ACTION",
                })
              }
            >
              Move to Next Stage
            </Button>
          </>
        }
      />

      <StageSelector
        open={config.open && config.type === "ACTION"}
        onClose={handleClose}
        selectedStage={selectedStage}
        setSelectedStage={setSelectedStage}
        onProceed={() => {
          if (selectedStage === "assessment") {
            handleOpen({
              type: "ASSIGN_ASSESSMENT",
              data: shortlistedData?.data?.data?.filter((item) =>
                selectedData.includes(item.id),
              ),
            });
            setSelectedStage("assessment");
          } else {
            handleOpen({
              type: "ASSIGN_INTERVIEW",
              data: shortlistedData?.data?.data?.filter((item) =>
                selectedData.includes(item.id),
              ),
            });
            setSelectedStage("assessment");
          }
        }}
      />
      <SetupInterview
        data={config.data}
        open={config.open && config.type === "ASSIGN_INTERVIEW"}
        handleClose={handleClose}
      />
      <AssignModal
        open={config.open && config.type === "ASSIGN_ASSESSMENT"}
        employees={config.data}
        handleClose={handleClose}
        onSuccess={() => setSelectedData([])}
      />
    </div>
  );
}
